/**
 * Machining charge engine: finds each individual cut (a contiguous run along
 * one grid line of a cutlist layout, gaps and all - see sheetCutSegments),
 * prices it as thickness^exponent per 1000mm of its own length (capped at
 * maxRate, floored at minCharge per cut), sums those, and picks the
 * "Machining Charge" product variant(s) needed to add that exact amount to
 * the cart.
 *
 * The variant catalog is a fixed price ladder from £0.05 to £100.00 in 5p
 * steps, split across 8 products of 250 variants each (Liquid/AJAX can't
 * list more than 250 variants per product). window.MachiningChargeTable is
 * rendered by snippets/machining-charge-table.liquid.
 */
(function (window) {
  'use strict';

  var EPS = 1e-6;
  var MAX_SHEETS = 100;
  var MAX_TOTAL_PIECES = 500;
  var MAX_LINE_PENCE = 10000; // £100.00
  var MIN_LINE_PENCE = 5; // £0.05

  /**
   * Merges [start, end] segments lying on the same line and returns the
   * total length they cover, counting any overlapping/touching stretch once.
   */
  function mergedLength(segments) {
    if (segments.length === 0) return 0;

    var sorted = segments.slice().sort(function (a, b) {
      return a[0] - b[0];
    });
    var total = 0;
    var curStart = sorted[0][0];
    var curEnd = sorted[0][1];

    for (var i = 1; i < sorted.length; i++) {
      var s = sorted[i][0];
      var e = sorted[i][1];
      if (s <= curEnd + EPS) {
        if (e > curEnd) curEnd = e;
      } else {
        total += curEnd - curStart;
        curStart = s;
        curEnd = e;
      }
    }

    total += curEnd - curStart;
    return total;
  }

  function rectsOverlap(a, b) {
    return !(
      a.x + a.w <= b.x + EPS ||
      b.x + b.w <= a.x + EPS ||
      a.y + a.h <= b.y + EPS ||
      b.y + b.h <= a.y + EPS
    );
  }

  function isValidSheet(sheet) {
    if (!sheet || typeof sheet.width !== 'number' || typeof sheet.height !== 'number') return false;
    if (!(sheet.width > 0) || !(sheet.height > 0)) return false;
    if (!Array.isArray(sheet.pieces)) return false;

    for (var i = 0; i < sheet.pieces.length; i++) {
      var p = sheet.pieces[i];
      if (!p || typeof p.x !== 'number' || typeof p.y !== 'number' || typeof p.w !== 'number' || typeof p.h !== 'number') {
        return false;
      }
      if (!(p.w > 0) || !(p.h > 0)) return false;
      if (p.x < -EPS || p.y < -EPS) return false;
      if (p.x + p.w > sheet.width + EPS) return false;
      if (p.y + p.h > sheet.height + EPS) return false;
    }

    for (var a = 0; a < sheet.pieces.length; a++) {
      for (var b = a + 1; b < sheet.pieces.length; b++) {
        if (rectsOverlap(sheet.pieces[a], sheet.pieces[b])) return false;
      }
    }

    return true;
  }

  /**
   * Every individual cut on a sheet: one entry per unique grid line (a fixed
   * x or y a piece edge sits on), its length being that line's full merged
   * extent even if it covers a gap - a gap-spanning line still counts as one
   * cut, per how this shop's saw actually runs a single pass along it.
   * Excludes edges flush with the sheet's own boundary (never cut).
   */
  function sheetCutSegments(sheet) {
    var verticalByX = {};
    var horizontalByY = {};

    function keyOf(v) {
      return Math.round(v * 100);
    }

    for (var i = 0; i < sheet.pieces.length; i++) {
      var p = sheet.pieces[i];
      var left = p.x;
      var right = p.x + p.w;
      var top = p.y;
      var bottom = p.y + p.h;

      if (left > EPS) {
        var kLeft = keyOf(left);
        (verticalByX[kLeft] = verticalByX[kLeft] || []).push([top, bottom]);
      }
      if (right < sheet.width - EPS) {
        var kRight = keyOf(right);
        (verticalByX[kRight] = verticalByX[kRight] || []).push([top, bottom]);
      }
      if (top > EPS) {
        var kTop = keyOf(top);
        (horizontalByY[kTop] = horizontalByY[kTop] || []).push([left, right]);
      }
      if (bottom < sheet.height - EPS) {
        var kBottom = keyOf(bottom);
        (horizontalByY[kBottom] = horizontalByY[kBottom] || []).push([left, right]);
      }
    }

    var cuts = [];
    for (var kx in verticalByX) {
      if (Object.prototype.hasOwnProperty.call(verticalByX, kx)) cuts.push(mergedLength(verticalByX[kx]));
    }
    for (var ky in horizontalByY) {
      if (Object.prototype.hasOwnProperty.call(horizontalByY, ky)) cuts.push(mergedLength(horizontalByY[ky]));
    }
    return cuts;
  }

  /**
   * Validates a cutlist layout ({ thickness, sheets: [{width, height, pieces}] })
   * and returns every individual cut's length (mm) across all its sheets, or
   * null if malformed/oversized.
   */
  function computeCutSegments(layout) {
    if (!layout || typeof layout.thickness !== 'number' || !(layout.thickness > 0)) return null;
    if (!Array.isArray(layout.sheets) || layout.sheets.length === 0) return null;
    if (layout.sheets.length > MAX_SHEETS) return null;

    var totalPieces = 0;
    var allSegments = [];

    for (var i = 0; i < layout.sheets.length; i++) {
      var sheet = layout.sheets[i];
      if (!isValidSheet(sheet)) return null;

      totalPieces += sheet.pieces.length;
      if (totalPieces > MAX_TOTAL_PIECES) return null;

      allSegments = allSegments.concat(sheetCutSegments(sheet));
    }

    return allSegments;
  }

  /**
   * Same validation as computeCutSegments, but returns the total cut length
   * (mm) across the whole layout - for display, not for pricing.
   */
  function computeCutLength(layout) {
    var segments = computeCutSegments(layout);
    if (segments === null) return null;
    return segments.reduce(function (sum, length) {
      return sum + length;
    }, 0);
  }

  /**
   * Builds a pence -> variantId lookup from the rendered price table.
   */
  function buildTableIndex(table) {
    var index = {};
    for (var i = 0; i < table.length; i++) {
      index[table[i].pence] = table[i].variantId;
    }
    return index;
  }

  /**
   * The pence amount actually charged for a given raw GBP figure: rounded up
   * to the nearest 5p, clamped to a 5p minimum. This is the single source
   * of truth both for picking cart variants and for what should be shown to
   * the customer, so the product page and the cart never disagree.
   */
  function chargePence(chargeGBP) {
    if (!(chargeGBP > 0)) return 0;
    var pence = Math.ceil((chargeGBP * 100) / 5) * 5;
    return Math.max(pence, MIN_LINE_PENCE);
  }

  /**
   * The GBP amount that will actually be charged for a given raw GBP figure
   * once rounded up to the nearest 10p (with a 10p minimum) - use this for
   * any customer-facing display of the machining charge.
   */
  function roundChargeGBP(chargeGBP) {
    return chargePence(chargeGBP) / 100;
  }

  /**
   * Picks the Machining Charge variant(s)/quantities needed to add an exact
   * charge (in GBP) to the cart. Rounds up to the nearest 10p, clamps to a
   * 10p minimum, and splits anything over £200 into one or more £200 lines
   * plus a remainder line.
   *
   * @param {number} chargeGBP
   * @param {{pence:number, variantId:number}[]} table
   * @returns {{id:number, quantity:number}[]}
   */
  function pickMachiningCartItems(chargeGBP, table) {
    var pence = chargePence(chargeGBP);
    if (pence <= 0) return [];

    var lines = [];
    var remaining = pence;

    if (remaining > MAX_LINE_PENCE) {
      var fullLines = Math.floor(remaining / MAX_LINE_PENCE);
      lines.push({ pence: MAX_LINE_PENCE, quantity: fullLines });
      remaining -= fullLines * MAX_LINE_PENCE;
    }
    if (remaining > 0) {
      lines.push({ pence: remaining, quantity: 1 });
    }

    var index = buildTableIndex(table);
    var items = [];
    for (var i = 0; i < lines.length; i++) {
      var variantId = index[lines[i].pence];
      if (!variantId) {
        throw new Error('No Machining Charge variant found for £' + (lines[i].pence / 100).toFixed(2));
      }
      items.push({ id: variantId, quantity: lines[i].quantity });
    }
    return items;
  }

  /**
   * The rate for a single cut, expressed per 1000mm of that cut's own
   * length: thickness^exponent, capped at pricing.maxRate. There's no floor
   * here - the per-cut minimum charge is applied after this rate is scaled
   * by each cut's actual length, not to the rate itself.
   */
  function sheetRatePerThousandMm(thickness, pricing) {
    return Math.min(Math.pow(thickness, pricing.exponent), pricing.maxRate);
  }

  /**
   * Convenience wrapper: given a cutlist layout and pricing
   * ({exponent, minCharge, maxRate}), returns the charge in GBP (each
   * individual cut priced at thickness^exponent per 1000mm of its own
   * length, capped at maxRate, then floored at minCharge per cut) and the
   * cart items to add. Returns null if the layout is invalid.
   */
  function computeMachiningCharge(layout, pricing, table) {
    var segments = computeCutSegments(layout);
    if (segments === null) return null;

    var rate = sheetRatePerThousandMm(layout.thickness, pricing);

    var chargeGBP = segments.reduce(function (sum, length) {
      return sum + Math.max(rate * (length / 1000), pricing.minCharge);
    }, 0);
    if (!isFinite(chargeGBP) || chargeGBP < 0) return null;

    var cutLength = segments.reduce(function (sum, length) {
      return sum + length;
    }, 0);

    return {
      chargeGBP: chargeGBP,
      chargeGBPRounded: roundChargeGBP(chargeGBP),
      cutLength: cutLength,
      cutCount: segments.length,
      cartItems: pickMachiningCartItems(chargeGBP, table),
    };
  }

  window.MachiningCharge = {
    computeCutLength: computeCutLength,
    roundChargeGBP: roundChargeGBP,
    pickMachiningCartItems: pickMachiningCartItems,
    computeMachiningCharge: computeMachiningCharge,
  };
})(window);
